import { InviteInfo } from '@squats/shared';
import { useEffect, useState, type FormEvent } from 'react';
import { useLocation } from 'wouter';
import { api, ApiError } from '../api.ts';
import { useAuth } from '../auth.tsx';
import { Centered } from '../components/Layout.tsx';
import { formatDateTime } from '../hooks.ts';
import { createPasskey, passkeyErrorMessage } from '../passkeys.ts';

type Load = { state: 'loading' } | { state: 'invalid'; message: string } | { state: 'ready'; info: InviteInfo };

export function InvitePage({ token }: { token: string }) {
  const { state: auth, setUser, signOut } = useAuth();
  const [, navigate] = useLocation();
  const [load, setLoad] = useState<Load>({ state: 'loading' });
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api('/api/auth/invite', InviteInfo, { body: { token } })
      .then((info) => {
        setLoad({ state: 'ready', info });
        if (info.purpose === 'signup' && info.name) setName(info.name);
      })
      .catch((e: unknown) => setLoad({ state: 'invalid', message: e instanceof ApiError ? e.message : String(e) }));
  }, [token]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (load.state !== 'ready') return;
    setBusy(true);
    setError(null);
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const user = await createPasskey(
        load.info.purpose === 'signup' ? { token, displayName: name, timezone } : { token },
      );
      setUser(user);
      navigate(load.info.purpose === 'signup' ? '/' : '/settings', { replace: true });
    } catch (err) {
      setError(passkeyErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  if (load.state === 'loading') {
    return (
      <Centered>
        <p className="muted">Checking your invite…</p>
      </Centered>
    );
  }

  if (load.state === 'invalid') {
    return (
      <Centered>
        <section className="card stack">
          <h2>Invite not usable</h2>
          <p>{load.message}</p>
          <p className="muted small">Ask whoever sent it for a new link.</p>
        </section>
      </Centered>
    );
  }

  const { info } = load;
  const signedInAs = auth.status === 'signedIn' ? auth.user : null;
  const isSignup = info.purpose === 'signup';
  // A sign-up link needs you signed out; a recovery link needs you signed out or signed in as its owner.
  const blocked = signedInAs && (isSignup || signedInAs.displayName !== info.name);

  if (blocked) {
    return (
      <Centered>
        <section className="card stack">
          <h2>{isSignup ? 'You’re already signed in' : `This link is for ${info.name}`}</h2>
          <p>
            You’re signed in as <strong>{signedInAs.displayName}</strong>. Sign out first to use this link on this
            device.
          </p>
          <button type="button" className="btn" onClick={() => void signOut()}>
            Sign out
          </button>
        </section>
      </Centered>
    );
  }

  return (
    <Centered>
      <form className="card stack" onSubmit={submit}>
        <h2>{isSignup ? 'You’re invited' : `Add a passkey for ${info.name}`}</h2>
        {isSignup ? (
          <label className="field">
            <span>Your name</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="given-name"
              maxLength={50}
              required
              autoFocus
            />
          </label>
        ) : (
          <p>This adds a passkey to the existing account, for example on a new phone.</p>
        )}
        <button type="submit" className="btn primary block" disabled={busy || (isSignup && !name.trim())}>
          {busy ? 'Waiting for your passkey…' : 'Create passkey'}
        </button>
        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
        <p className="muted small">
          Your phone will offer to save a passkey, confirmed with Face ID or your fingerprint. That’s how you’ll sign in:
          no password. On a computer, choose <strong>Use a phone or tablet</strong> and scan the QR code so the passkey
          is saved on your phone.
        </p>
        <p className="muted small">This link works once and expires {formatDateTime(info.expiresAt)}.</p>
      </form>
    </Centered>
  );
}
